import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../authentication/AuthContext";
import {
  ImageUpload, TagInput, InfoTooltip, RefSelect, ConsequenceCard, TAG_SUGGESTIONS,
  SCORE_TYPES, SCORE_TYPE_LABELS, SCORE_TYPE_DESC, returnTarget, saveDecisionWithGuards,
} from "./BuilderUtils";
import "../../builder.css";

const emptyDecision = {
  _id: "",
  title: "",
  description: "",
  image_url: "",
  tags: [],
  source: [],
  score: {},
  add_elem_to_pile_if_taken: [],
  add_elem_to_pile_if_not_taken: [],
};

const emptyConsequence = { condition: false, consequence_id: "" };

const SCORE_TOOLTIPS = {
  impact: "Valeur numérique de l'impact. Positif = amélioration, négatif = dégradation. L'amplitude dépend de l'échelle du score.",
  comment: "Justification de l'impact affichée aux participants quand ils survolent la carte de décision.",
  position: "Pour les acteurs en mode ACG : 'positive' = l'acteur soutient, 'negative' = l'acteur s'y oppose. N'affecte pas le calcul numérique.",
};

/**
 * DecisionEditor — standalone (route /builder/decision/:decisionId)
 *                  or embedded (props embeddedId + onClose)
 */
export default function DecisionEditor({ embeddedId, onClose, parentConfig }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const decisionId = embeddedId !== undefined ? embeddedId : params.decisionId;
  const isNew = decisionId === "new";
  const isEmbedded = embeddedId !== undefined;

  const [decision, setDecision] = useState(emptyDecision);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [allPages, setAllPages] = useState([]);
  const [allDecisions, setAllDecisions] = useState([]);
  const [allConfigs, setAllConfigs] = useState([]);

  const [showScoreForm, setShowScoreForm] = useState(false);
  const [editingScoreKey, setEditingScoreKey] = useState(null);
  const [scoreFormData, setScoreFormData] = useState({ key: "", impact: 0, comment: "", position: "" });

  // Ateliers qui utilisent réellement cette décision (null = pas encore chargé).
  const [usedByConfigs, setUsedByConfigs] = useState(null);

  const scoreOptions = Object.entries(parentConfig?.score_dic || {}).map(([key, s]) => ({
    value: key, label: s.name, sublabel: s.type,
  }));
  const pageOptions = allPages.map((p) => ({ value: p._id, label: p.title, sublabel: p.type }));

  const apiGet = async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      headers: { Authorization: "Bearer " + token },
    });
    return res.json();
  };

  const apiDelete = async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    });
    return res.json();
  };

  useEffect(() => {
    apiGet("/get_all_pages").then(setAllPages);
    apiGet("/get_all_game_configs").then(setAllConfigs);
    apiGet("/get_all_decisions").then((all) => {
      setAllDecisions(all);
      if (!isNew) {
        const found = all.find((d) => d._id === decisionId);
        if (found) setDecision({ ...emptyDecision, ...found });
        setLoading(false);
      }
    });
    if (!isNew) {
      apiGet(`/get_configs_using_decision/${decisionId}`).then((r) => setUsedByConfigs(r.used_by || []));
    } else {
      setUsedByConfigs([]);
    }
  }, [decisionId]);

  const set = (field, value) => setDecision((d) => ({ ...d, [field]: value }));

  // Score impacts
  const openNewScore = () => {
    setEditingScoreKey(null);
    setScoreFormData({ key: "", impact: 0, comment: "", position: "", metadata: null });
    setShowScoreForm(true);
  };

  const openEditScore = (key) => {
    const s = decision.score[key] || {};
    setEditingScoreKey(key);
    setScoreFormData({ key, impact: s.impact ?? 0, comment: s.comment || "", position: s.position || "", metadata: s.metadata || null });
    setShowScoreForm(true);
  };

  // Sous-catégories d'un score composite, si le score choisi en a (via score_dic de l'atelier parent)
  const compositeSubcategories = (key) => {
    const meta = (parentConfig?.score_dic || {})[key]?.metadata;
    return meta?.standard ? Object.keys(meta.standard) : null;
  };

  const toggleDetailedImpact = (enabled, key) => {
    if (enabled) {
      const subcats = compositeSubcategories(key) || [];
      const metadata = Object.fromEntries(subcats.map((name) => [name, 0]));
      setScoreFormData((s) => ({ ...s, metadata }));
    } else {
      setScoreFormData((s) => ({ ...s, metadata: null }));
    }
  };

  const updateDetailedImpact = (name, value) => {
    setScoreFormData((s) => {
      const metadata = { ...(s.metadata || {}), [name]: value };
      const total = Object.values(metadata).reduce((sum, v) => sum + (Number(v) || 0), 0);
      return { ...s, metadata, impact: total };
    });
  };

  const saveScore = () => {
    if (!scoreFormData.key.trim()) return alert("L'identifiant du score est requis.");
    const newScore = { ...decision.score };
    if (editingScoreKey && editingScoreKey !== scoreFormData.key) delete newScore[editingScoreKey];
    const entry = { impact: Number(scoreFormData.impact) };
    if (scoreFormData.comment.trim()) entry.comment = scoreFormData.comment;
    if (scoreFormData.position) entry.position = scoreFormData.position;
    if (scoreFormData.metadata) entry.metadata = scoreFormData.metadata;
    newScore[scoreFormData.key] = entry;
    set("score", newScore);
    setShowScoreForm(false);
  };

  const deleteScore = (key) => {
    const newScore = { ...decision.score };
    delete newScore[key];
    set("score", newScore);
    if (showScoreForm && editingScoreKey === key) setShowScoreForm(false);
  };

  // Consequences if taken
  const addTaken = () => set("add_elem_to_pile_if_taken", [...(decision.add_elem_to_pile_if_taken || []), { ...emptyConsequence }]);
  const updateTaken = (idx, f, v) => {
    const list = [...(decision.add_elem_to_pile_if_taken || [])];
    list[idx] = { ...list[idx], [f]: v };
    set("add_elem_to_pile_if_taken", list);
  };
  const removeTaken = (idx) => {
    const list = [...(decision.add_elem_to_pile_if_taken || [])];
    list.splice(idx, 1);
    set("add_elem_to_pile_if_taken", list);
  };

  // Consequences if not taken
  const addNotTaken = () => set("add_elem_to_pile_if_not_taken", [...(decision.add_elem_to_pile_if_not_taken || []), { ...emptyConsequence }]);
  const updateNotTaken = (idx, f, v) => {
    const list = [...(decision.add_elem_to_pile_if_not_taken || [])];
    list[idx] = { ...list[idx], [f]: v };
    set("add_elem_to_pile_if_not_taken", list);
  };
  const removeNotTaken = (idx) => {
    const list = [...(decision.add_elem_to_pile_if_not_taken || [])];
    list.splice(idx, 1);
    set("add_elem_to_pile_if_not_taken", list);
  };

  const backTo = returnTarget(location);

  const goBack = () => {
    if (isEmbedded) { onClose?.(); return; }
    navigate(backTo.path);
  };

  const handleSave = async () => {
    setSaving(true);
    const outcome = await saveDecisionWithGuards(decision, {
      token, isNew, existingIds: allDecisions.map((d) => d._id), usedByConfigs,
    });
    setSaving(false);
    if (outcome.ok) {
      alert("Décision sauvegardée !");
      if (isEmbedded) onClose?.();
      else {
        const returnTab = location.state?.returnTab;
        navigate(`/builder${returnTab ? `?tab=${returnTab}` : ""}`);
      }
      return;
    }
    if (!outcome.cancelled) alert("Erreur : " + outcome.message);
  };

  const handleDelete = async () => {
    // Le même décompte que l'API, qui refuse la suppression sans `force` :
    // les mesures d'un réservoir n'apparaissent dans aucune `list_decisions`.
    const usedIn = (await apiGet(`/get_configs_using_decision/${decision._id}`)).used_by || [];
    const warning = usedIn.length > 0
      ? `\n\n⚠ Cette décision est employée par ${usedIn.length} atelier(s) : ${usedIn.join(", ")}. La supprimer l'en retirera.`
      : "";
    if (!window.confirm(`Supprimer la décision "${decision._id}" ?${warning}`)) return;
    const res = await apiDelete(`/delete_decision/${decision._id}?force=true`);
    if (res.success) {
      if (isEmbedded) { onClose?.(); }
      else {
        const returnTab = location.state?.returnTab;
        navigate(`/builder${returnTab ? `?tab=${returnTab}` : ""}`);
      }
    } else alert("Erreur : " + res.message);
  };

  const impactColor = (v) => v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#94a3b8";
  const impactBg = (v) => v > 0 ? "#dcfce7" : v < 0 ? "#fee2e2" : "#f1f5f9";

  const scoreType = (key) => (parentConfig?.score_dic || {})[key]?.type;

  const sortedScores = Object.entries(decision.score || {}).sort(([, a], [, b]) => Math.abs(b.impact) - Math.abs(a.impact));

  // Groupe les impacts par type de score (metric/actor/cbs-submetric/custom) pour plus de clarté,
  // car un impact sur un acteur (soutien/opposition) n'a pas la même sémantique qu'un impact sur une métrique.
  const scoresByType = SCORE_TYPES.reduce((acc, t) => {
    acc[t] = sortedScores.filter(([key]) => scoreType(key) === t);
    return acc;
  }, {});
  const untypedScores = sortedScores.filter(([key]) => !SCORE_TYPES.includes(scoreType(key)));

  if (loading) return <div className="builder-loading">Chargement…</div>;

  const sections = (
    <>
      {!isNew && usedByConfigs !== null && usedByConfigs.length > 0 && (
        <div className="builder-usage-banner">
          ⚠ Décision utilisée dans <strong>{usedByConfigs.length} atelier{usedByConfigs.length > 1 ? "s" : ""}</strong> :
          {" "}{usedByConfigs.join(", ")}. Toute modification s'appliquera à tous ces ateliers.
        </div>
      )}

      {/* ── Infos générales ── */}
      <section className="builder-section">
        <h2 className="builder-section-title">Informations générales</h2>

        <div className="decision-editor-layout">
          {/* Colonne gauche : champs texte */}
          <div className="decision-editor-fields">
            <div className="builder-form-grid">
              <label className="builder-label">
                <span className="field-label-row">
                  Identifiant <InfoTooltip text="Slug unique dans la collection decisions. Utilisé dans les listes de décisions des pages et les conséquences." />
                </span>
                <input className="builder-input" value={decision._id}
                  onChange={(e) => set("_id", e.target.value)} disabled={!isNew}
                  placeholder="ex: vegetaliser-toiture" />
              </label>

              <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
                <span className="field-label-row">
                  Titre <InfoTooltip text="Titre affiché sur la carte de décision dans le jeu." />
                </span>
                <input className="builder-input" value={decision.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="Ex: Végétaliser les toitures" />
              </label>

              <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
                <span className="field-label-row">
                  Description <InfoTooltip text="Texte détaillé affiché quand le participant ouvre la carte de décision pour en savoir plus." />
                </span>
                <textarea className="builder-textarea" rows={4} value={decision.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Décrivez la mesure, ses avantages et ses limites…" />
              </label>
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="field-label-row" style={{ marginBottom: 8 }}>
                <span className="builder-label-text">Tags</span>
                <InfoTooltip text="Catégories de la décision. Utilisées pour filtrer les décisions disponibles par type d'atelier." />
              </div>
              <TagInput tags={decision.tags || []} onChange={(t) => set("tags", t)} suggestions={TAG_SUGGESTIONS} />
            </div>
          </div>

          {/* Colonne droite : image */}
          <div className="decision-editor-image">
            <ImageUpload
              label="Image de la décision"
              value={decision.image_url}
              onChange={(url) => set("image_url", url)}
              token={token}
            />
          </div>
        </div>
      </section>

      {/* ── Impacts sur les scores ── */}
      <section className="builder-section">
        <div className="builder-section-header">
          <h2 className="builder-section-title">
            <span className="field-label-row">
              Impacts sur les scores ({Object.keys(decision.score || {}).length})
              <InfoTooltip text="Effets de la décision sur chaque score du jeu. Les impacts sont appliqués quand la décision est sélectionnée par les participants." />
            </span>
          </h2>
          <button className="builder-btn-secondary" onClick={openNewScore}>+ Ajouter un impact</button>
        </div>

        {/* Score form modal */}
        {showScoreForm && (
          <div className="builder-picker-overlay" onClick={() => setShowScoreForm(false)}>
            <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
              <div className="builder-picker-header">
                <h3>
                  {editingScoreKey ? `Éditer impact : ${editingScoreKey}` : "Nouvel impact"}
                  {scoreType(scoreFormData.key) && (
                    <span className={`builder-tag tag-${scoreType(scoreFormData.key)}`} style={{ marginLeft: 8, verticalAlign: "middle" }}>
                      {SCORE_TYPE_LABELS[scoreType(scoreFormData.key)]}
                    </span>
                  )}
                </h3>
                <button onClick={() => setShowScoreForm(false)}>✕</button>
              </div>
              <div className="builder-picker-body">
                <div className="builder-form-grid">
                  <label className="builder-label">
                    <span className="field-label-row">
                      Score ID <InfoTooltip text="Identifiant du score dans le score_dic de l'atelier." />
                    </span>
                    {editingScoreKey ? (
                      <input className="builder-input" value={scoreFormData.key} disabled />
                    ) : (
                      <RefSelect
                        value={scoreFormData.key}
                        onChange={(v) => setScoreFormData((s) => ({ ...s, key: v }))}
                        options={scoreOptions}
                        placeholder="Choisir un score…"
                      />
                    )}
                  </label>

                  <label className="builder-label">
                    <span className="field-label-row">
                      Impact <InfoTooltip text={scoreFormData.metadata ? "Calculé automatiquement : somme des impacts détaillés par sous-catégorie ci-dessous." : SCORE_TOOLTIPS.impact} />
                    </span>
                    <div className="impact-input-wrapper">
                      <button
                        className="impact-step-btn"
                        disabled={!!scoreFormData.metadata}
                        onClick={() => setScoreFormData((s) => ({ ...s, impact: Number(s.impact) - 1 }))}
                      >−</button>
                      <input
                        className="builder-input impact-input"
                        type="number"
                        disabled={!!scoreFormData.metadata}
                        value={scoreFormData.impact}
                        onChange={(e) => setScoreFormData((s) => ({ ...s, impact: e.target.value }))}
                      />
                      <button
                        className="impact-step-btn"
                        disabled={!!scoreFormData.metadata}
                        onClick={() => setScoreFormData((s) => ({ ...s, impact: Number(s.impact) + 1 }))}
                      >+</button>
                    </div>
                    <div className="impact-visual"
                      style={{ background: impactBg(Number(scoreFormData.impact)), color: impactColor(Number(scoreFormData.impact)) }}>
                      {Number(scoreFormData.impact) > 0 ? "▲ Positif" : Number(scoreFormData.impact) < 0 ? "▼ Négatif" : "◆ Neutre"}
                      {" · "}
                      <strong>{scoreFormData.impact > 0 ? "+" : ""}{scoreFormData.impact}</strong>
                    </div>
                  </label>

                  {compositeSubcategories(scoreFormData.key) && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label className="builder-label" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                        <input type="checkbox" checked={!!scoreFormData.metadata}
                          onChange={(e) => toggleDetailedImpact(e.target.checked, scoreFormData.key)} />
                        <span className="field-label-row">
                          Détailler l'impact par sous-catégorie
                          <InfoTooltip text="Ce score est composite. Répartissez l'impact entre ses sous-catégories (ex: -2 sur 'Revêtement' plutôt qu'un impact global)." />
                        </span>
                      </label>
                      {scoreFormData.metadata && (
                        <div style={{ marginTop: 8, padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                          {compositeSubcategories(scoreFormData.key).map((name) => (
                            <div key={name} className="builder-inline-row">
                              <span className="builder-label-text" style={{ minWidth: 160 }}>{name}</span>
                              <input className="builder-input small" type="number"
                                value={scoreFormData.metadata[name] ?? 0}
                                onChange={(e) => updateDetailedImpact(name, Number(e.target.value))} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
                    <span className="field-label-row">
                      Commentaire <InfoTooltip text={SCORE_TOOLTIPS.comment} />
                    </span>
                    <textarea className="builder-textarea" rows={3} value={scoreFormData.comment}
                      onChange={(e) => setScoreFormData((s) => ({ ...s, comment: e.target.value }))}
                      placeholder="Justification visible par les participants…" />
                  </label>

                  {scoreType(scoreFormData.key) === "actor" && (
                    <label className="builder-label">
                      <span className="field-label-row">
                        Position acteur <InfoTooltip text={SCORE_TOOLTIPS.position} />
                      </span>
                      <select className="builder-input" value={scoreFormData.position}
                        onChange={(e) => setScoreFormData((s) => ({ ...s, position: e.target.value }))}>
                        <option value="">— Non définie —</option>
                        <option value="positive">Soutient (positive)</option>
                        <option value="negative">S'oppose (negative)</option>
                      </select>
                    </label>
                  )}
                </div>
              </div>
              <div className="builder-picker-footer">
                {editingScoreKey && (
                  <button className="builder-btn-danger" onClick={() => { deleteScore(editingScoreKey); setShowScoreForm(false); }}>
                    Supprimer
                  </button>
                )}
                <button className="builder-btn-secondary" onClick={() => setShowScoreForm(false)}>Annuler</button>
                <button className="builder-btn-primary" onClick={saveScore}>Valider</button>
              </div>
            </div>
          </div>
        )}

        {/* Score impact cards, groupées par type de score */}
        {sortedScores.length > 0 ? (
          <>
            {SCORE_TYPES.map((type) => {
              const entries = scoresByType[type];
              if (entries.length === 0) return null;
              return (
                <div key={type} className="score-group">
                  <div className="score-group-header">
                    <span className={`builder-tag tag-${type}`}>{SCORE_TYPE_LABELS[type]}</span>
                    <span className="score-group-desc">{SCORE_TYPE_DESC[type]}</span>
                    <span className="score-group-count">{entries.length}</span>
                  </div>
                  <div className="score-impacts-grid">
                    {entries.map(([key, s]) => (
                      <ScoreImpactCard key={key} scoreKey={key} score={s}
                        impactColor={impactColor} impactBg={impactBg}
                        onClick={() => openEditScore(key)} />
                    ))}
                  </div>
                </div>
              );
            })}
            {untypedScores.length > 0 && (
              <div className="score-group">
                <div className="score-group-header">
                  <span className="builder-tag">Autres</span>
                  <span className="score-group-desc">Score non trouvé dans le dictionnaire de l'atelier (peut-être supprimé depuis).</span>
                  <span className="score-group-count">{untypedScores.length}</span>
                </div>
                <div className="score-impacts-grid">
                  {untypedScores.map(([key, s]) => (
                    <ScoreImpactCard key={key} scoreKey={key} score={s}
                      impactColor={impactColor} impactBg={impactBg}
                      onClick={() => openEditScore(key)} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="builder-empty">Aucun impact défini. Commencez par définir les effets de cette décision.</div>
        )}
      </section>

      {/* ── Conséquences si PRISE ── */}
      <section className="builder-section">
        <div className="builder-section-header">
          <h2 className="builder-section-title">
            <span className="field-label-row">
              Si décision prise → pages ajoutées ({(decision.add_elem_to_pile_if_taken || []).length})
              <InfoTooltip text="Pages insérées dans la pile lorsque cette décision est sélectionnée. Peuvent être conditionnelles à une décision précédente." />
            </span>
          </h2>
          <button className="builder-btn-secondary" onClick={addTaken}>+ Ajouter</button>
        </div>
        {(decision.add_elem_to_pile_if_taken || []).length === 0 && (
          <div className="builder-empty">Aucune conséquence si prise</div>
        )}
        {(decision.add_elem_to_pile_if_taken || []).map((c, idx) => (
          <ConsequenceRow key={idx} consequence={c}
            onChange={(f, v) => updateTaken(idx, f, v)}
            onRemove={() => removeTaken(idx)}
            pageOptions={pageOptions} decisionOptions={allDecisions.map((d) => ({ value: d._id, label: d.title }))}
            allConfigs={allConfigs} />
        ))}
      </section>

      {/* ── Conséquences si NON PRISE ── */}
      <section className="builder-section">
        <div className="builder-section-header">
          <h2 className="builder-section-title">
            <span className="field-label-row">
              Si décision non prise → pages ajoutées ({(decision.add_elem_to_pile_if_not_taken || []).length})
              <InfoTooltip text="Pages insérées dans la pile lorsque cette décision N'est PAS sélectionnée. Utilisé pour montrer les conséquences d'un non-choix." />
            </span>
          </h2>
          <button className="builder-btn-secondary" onClick={addNotTaken}>+ Ajouter</button>
        </div>
        {(decision.add_elem_to_pile_if_not_taken || []).length === 0 && (
          <div className="builder-empty">Aucune conséquence si non prise</div>
        )}
        {(decision.add_elem_to_pile_if_not_taken || []).map((c, idx) => (
          <ConsequenceRow key={idx} consequence={c}
            onChange={(f, v) => updateNotTaken(idx, f, v)}
            onRemove={() => removeNotTaken(idx)}
            pageOptions={pageOptions} decisionOptions={allDecisions.map((d) => ({ value: d._id, label: d.title }))}
            allConfigs={allConfigs} />
        ))}
      </section>
    </>
  );

  // ── Embedded mode ───────────────────────────────────────────────────
  if (isEmbedded) {
    return (
      <div className="embedded-editor">
        {sections}
        <div className="embedded-editor-footer">
          {!isNew && (
            <button className="builder-btn-danger" onClick={handleDelete}>Supprimer</button>
          )}
          <button className="builder-btn-secondary" onClick={onClose}>Annuler</button>
          <button className="builder-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Sauvegarde…" : "Sauvegarder"}
          </button>
        </div>
      </div>
    );
  }

  // ── Standalone mode ─────────────────────────────────────────────────
  return (
    <div className="builder-container">
      <div className="builder-header">
        <button className="builder-back-btn" onClick={goBack}>← {backTo.label}</button>
        <h1 className="builder-title">{isNew ? "Nouvelle décision" : `Éditer : ${decision._id}`}</h1>
        <div className="builder-header-actions">
          {!isNew && <button className="builder-btn-danger" onClick={handleDelete}>Supprimer</button>}
          <button className="builder-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Sauvegarde…" : "Sauvegarder"}
          </button>
        </div>
      </div>
      {sections}
    </div>
  );
}

function ScoreImpactCard({ scoreKey, score: s, impactColor, impactBg, onClick }) {
  return (
    <div
      className="score-impact-card"
      onClick={onClick}
      style={{ borderLeftColor: impactColor(s.impact) }}
    >
      <div className="score-impact-card-header">
        <span className="score-impact-key">
          {scoreKey}
          {s.metadata && <span className="builder-tag tag-type" style={{ marginLeft: 6 }}>détaillé</span>}
        </span>
        <span className="score-impact-badge"
          style={{ background: impactBg(s.impact), color: impactColor(s.impact) }}>
          {s.impact > 0 ? "+" : ""}{s.impact}
        </span>
      </div>
      {s.comment && <p className="score-impact-comment">{s.comment}</p>}
      {s.position && (
        <span className={`score-position-badge ${s.position}`}>
          {s.position === "positive" ? "▲ Soutient" : "▼ S'oppose"}
        </span>
      )}
    </div>
  );
}

function ConsequenceRow({ consequence, onChange, onRemove, pageOptions = [], decisionOptions = [], allConfigs = [] }) {
  const [showVariations, setShowVariations] = useState(false);
  const variations = consequence.variation_game_config || {};
  const hasVariations = Object.keys(variations).length > 0;

  const setVariation = (configId, value) => {
    const next = { ...variations, [configId]: value };
    onChange("variation_game_config", next);
  };
  const removeVariation = (configId) => {
    const next = { ...variations };
    delete next[configId];
    onChange("variation_game_config", Object.keys(next).length ? next : undefined);
  };
  const addVariationFor = (configId) => setVariation(configId, "");

  const availableConfigsToAdd = allConfigs.filter((c) => !(c._id in variations));

  return (
    <ConsequenceCard consequence={consequence} onRemove={onRemove} lookups={{
      pageLabel: (id) => pageOptions.find((p) => p.value === id)?.label,
      decisionLabel: (id) => decisionOptions.find((d) => d.value === id)?.label,
      pageExists: (id) => pageOptions.some((p) => p.value === id),
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <select className="builder-input small" value={String(consequence.condition)}
            onChange={(e) => onChange("condition", e.target.value === "true")}>
            <option value="false">Toujours</option>
            <option value="true">Si décision précédente</option>
          </select>

          {consequence.condition && (
            <RefSelect placeholder="Décision requise"
              value={consequence.previous_decision_id || ""}
              onChange={(v) => onChange("previous_decision_id", v)}
              options={decisionOptions} />
          )}

          <RefSelect placeholder="Page conséquence (par défaut)"
            value={consequence.consequence_id || ""}
            onChange={(v) => onChange("consequence_id", v)}
            options={pageOptions} />
        </div>

        {!consequence.consequence_id && (
          <p className="builder-hint consequence-empty-hint">
            ◆ Aucune page choisie ci-dessus : il n'y aura pas de conséquence par défaut
            {hasVariations ? " (sauf pour les ateliers listés ci-dessous)" : ""}.
          </p>
        )}

        <div>
          <button
            type="button"
            className="ref-select-fallback-hint"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#3b82f6", padding: 0 }}
            onClick={() => setShowVariations((v) => !v)}
          >
            {showVariations || hasVariations
              ? `▾ Variantes par atelier${hasVariations ? ` (${Object.keys(variations).length})` : ""}`
              : "▸ Ajouter une variante par atelier…"}
          </button>

          {(showVariations || hasVariations) && (
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
              <p className="builder-hint" style={{ margin: "2px 0 4px" }}>
                Pour un atelier donné, remplace la page conséquence par défaut ci-dessus (ou n'ajoute rien si vide).
              </p>
              {Object.entries(variations).map(([configId, pageIdOrNull]) => (
                <div key={configId} className="builder-inline-row">
                  <span className="builder-tag tag-type" style={{ minWidth: 140 }}>{configId}</span>
                  <RefSelect
                    value={pageIdOrNull || ""}
                    onChange={(v) => setVariation(configId, v || null)}
                    options={pageOptions}
                    placeholder="Aucune conséquence pour cet atelier"
                  />
                  <button className="builder-icon-btn danger" onClick={() => removeVariation(configId)} title="Retirer cette variante">✕</button>
                </div>
              ))}
              {availableConfigsToAdd.length > 0 && (
                <RefSelect
                  value=""
                  onChange={(v) => v && addVariationFor(v)}
                  options={availableConfigsToAdd.map((c) => ({ value: c._id, label: c.name }))}
                  placeholder="+ Choisir un atelier à surcharger…"
                  allowClear={false}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </ConsequenceCard>
  );
}
